import React, { useEffect, useRef, useState } from 'react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Check,
  Loader2,
  Lock,
  Sparkles,
  Vault,
  Calendar,
  Crown,
  Flame,
  ShieldCheck,
} from 'lucide-react';
import { loadStripe } from '@/lib/stripe';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/sonner';

import BillingPlanSelector from '@/components/membership/BillingPlanSelector';
import BillingSummaryCard from '@/components/membership/BillingSummaryCard';
import StripeCheckoutPanel from '@/components/membership/StripeCheckoutPanel';

import type { BillingCycle } from '@/contexts/MembershipContext';
import { MEMBERSHIP_PLANS, type PlanId } from '@/lib/business/KinsCircleBusinessEngine';

// ─────────────────────────────────────────────────────────────────────────
// MembershipModal — STRICTLY billing-cycle aware.
//
// Two hard-separated checkout flows live in this component:
//
//   • Monthly  →  Business Engine pricing with a secure family membership. Card collected today,
//                 not charged until trial ends. Trial logic ONLY applies
//                 to this branch.
//
//   • Yearly   →  Business Engine yearly pricing Extended Family Plan • Ends after first 1,000 families. NO trial, charged today,
//                 renews yearly. Trial flags are never attached to this
//                 cycle, on the client OR the edge function.
//
// The user must explicitly pick a cycle (either by clicking the right
// CTA in the Pricing section, or in the in-modal picker) before checkout
// starts. We never silently default to a trial.
// ─────────────────────────────────────────────────────────────────────────

type Step = 'choose' | 'details' | 'payment' | 'success';

type MembershipReceipt = {
  recovered: boolean;
  subscriptionId: string;
  planId: PlanId;
  billingCycle: BillingCycle;
  amount: number;
  seatsTotal: number;
  invoiceId: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
  receiptUrl: string | null;
};

interface MembershipModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPlanId?: PlanId;
  initialCycle?: BillingCycle;
}

const DEFAULT_MEMBERSHIP_PLAN_ID: PlanId = 'family_10';

const buildPlanMeta = (planId: PlanId) => {
  const plan = MEMBERSHIP_PLANS[planId];

  return {
    monthly: {
      label: `${plan.name} Monthly`,
      priceLabel: `${plan.monthlyPrice.toFixed(2)}`,
      suffix: '/month',
      perMonthHint:
        `${plan.seats} seats • ${plan.includedKinsSpaceGB} GB KinsSpace`,
      profilePlan: 'monthly_premium' as const,
      hasTrial: false,
    },
    yearly: {
      label: `${plan.name} Yearly`,
      priceLabel: `${plan.yearlyPrice.toFixed(2)}`,
      suffix: '/year',
      perMonthHint:
        `${plan.seats} seats • ${plan.includedKinsSpaceGB} GB KinsSpace`,
      profilePlan: 'annual_standard' as const,
      hasTrial: false,
    },
  };
};

const fmtUsd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const MembershipModal: React.FC<MembershipModalProps> = ({
  open,
  onOpenChange,
  initialPlanId,
  initialCycle,
}) => {
  const { user, profile, refreshProfile, updateProfile } = useAuth();

  // The user's chosen cycle. `null` means "haven't picked yet" → show picker.
  const [planId, setPlanId] = useState<PlanId>(
    initialPlanId ?? DEFAULT_MEMBERSHIP_PLAN_ID,
  );
  const [cycle, setCycle] =
    useState<BillingCycle | null>(initialCycle ?? null);
  const [step, setStep] =
    useState<Step>(initialCycle ? 'details' : 'choose');

  const [familyName, setFamilyNameLocal] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [purchaseAttemptId, setPurchaseAttemptId] =
    useState<string | null>(null);
  const [receipt, setReceipt] =
    useState<MembershipReceipt | null>(null);

  const stripeRef = useRef<any>(null);
  const elementsRef = useRef<any>(null);
  const paymentElRef = useRef<any>(null);
  const mountRef = useRef<HTMLDivElement>(null);

  const activePlan = MEMBERSHIP_PLANS[planId];
  const planMeta = buildPlanMeta(planId);
  const meta = cycle ? planMeta[cycle] : null;
  const totalAmount = cycle
    ? cycle === 'yearly'
      ? activePlan.yearlyPrice
      : activePlan.monthlyPrice
    : 0;
  const email = user?.email || '';
  const name = profile?.name || '';

  // Pre-fill family name from profile if available
  useEffect(() => {
    if (open && profile?.family_name && !familyName) {
      setFamilyNameLocal(profile.family_name);
    }
  }, [open, profile?.family_name, familyName]);

  // When the modal opens with a pre-selected cycle, jump past the picker.
  useEffect(() => {
    if (!open) return;
    setPlanId(initialPlanId ?? DEFAULT_MEMBERSHIP_PLAN_ID);

    if (initialCycle) {
      setCycle(initialCycle);
      setStep('details');
    } else {
      setCycle(null);
      setStep('choose');
    }
  }, [open, initialPlanId, initialCycle]);

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep(initialCycle ? 'details' : 'choose');
        setPlanId(initialPlanId ?? DEFAULT_MEMBERSHIP_PLAN_ID);
        setCycle(initialCycle ?? null);
        setFamilyNameLocal('');
        setError(null);
        setClientSecret(null);
        setCustomerId(null);
        setPurchaseAttemptId(null);
        setReceipt(null);
        if (paymentElRef.current) {
          try { paymentElRef.current.unmount(); } catch {}
          paymentElRef.current = null;
        }
        elementsRef.current = null;
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open, initialPlanId, initialCycle]);

  // Mount Stripe Elements ONLY after a non-empty clientSecret exists.
  // We never create elements before the backend has returned one — this is
  // what causes "Could not retrieve elements store due to unexpected error".
  useEffect(() => {
    if (step !== 'payment') return;
    if (!clientSecret || typeof clientSecret !== 'string' || clientSecret.length === 0) return;
    if (!mountRef.current) return;

    let cancelled = false;
    let createdPaymentEl: any = null;

    // Runtime diagnostic — logs the exact shape Stripe.js sees BEFORE elements()
    // is called, so we can spot client_secret/account mismatches at a glance.
    // eslint-disable-next-line no-console
    console.log('[membership] mount-elements:start', {
      typeof_clientSecret: typeof clientSecret,
      clientSecret_length: clientSecret.length,
      clientSecret_first15: clientSecret.slice(0, 15),
      intent_kind: clientSecret.startsWith('seti_')
        ? 'SetupIntent'
        : clientSecret.startsWith('pi_')
        ? 'PaymentIntent'
        : 'unknown',
    });

    (async () => {
      try {
        if (!stripeRef.current) stripeRef.current = await loadStripe();
        if (cancelled) return;
        if (!stripeRef.current) {
          setError('Payment system failed to load. Please refresh and try again.');
          return;
        }

        // Re-create elements every time clientSecret changes. Critical:
        // clientSecret MUST be passed in options, never undefined/empty.
        let createdElements: any;
        try {
          createdElements = stripeRef.current.elements({
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#78350f',
                colorBackground: '#ffffff',
                colorText: '#451a03',
                fontFamily: 'Inter, system-ui, sans-serif',
                borderRadius: '10px',
              },
            },
          });
        } catch (elErr: any) {
          // eslint-disable-next-line no-console
          console.error('[membership] elements() threw', {
            name: elErr?.name,
            message: elErr?.message,
            stack: elErr?.stack,
            raw: elErr,
          });
          if (!cancelled) {
            setError(
              elErr?.message ||
                'Stripe Elements failed to initialize. The publishable key and the backend secret may belong to different Stripe accounts.'
            );
          }
          return;
        }
        elementsRef.current = createdElements;
        // eslint-disable-next-line no-console
        console.log('[membership] elements() ok', { has_elements: !!createdElements });

        if (cancelled || !mountRef.current) return;

        createdPaymentEl = elementsRef.current.create('payment', { layout: 'tabs' });
        createdPaymentEl.mount(mountRef.current);
        paymentElRef.current = createdPaymentEl;
        // eslint-disable-next-line no-console
        console.log('[membership] payment-element mounted');
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('[membership] mount-elements:error', {
          name: err?.name,
          message: err?.message,
          stack: err?.stack,
        });
        if (!cancelled) {
          setError(err?.message || 'Failed to load payment form');
        }
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (paymentElRef.current) {
          paymentElRef.current.unmount();
          paymentElRef.current = null;
        }
      } catch {}
      elementsRef.current = null;
    };
  }, [step, clientSecret]);



  const startPurchaseAttempt = async (
    values: {
      setupIntentId: string | null;
      stripeCustomerId: string | null;
    },
  ) => {
    if (!user?.id || !cycle) {
      throw new Error(
        'Membership purchase authority is unavailable.',
      );
    }

    const {
      data,
      error: attemptError,
    } = await supabase.rpc(
      'start_billing_purchase_attempt',
      {
        p_product_type: 'membership',
        p_product_reference: planId,
        p_billing_cycle: cycle,
        p_amount_cents:
          Math.round(totalAmount * 100),
        p_currency: 'usd',
        p_stripe_customer_id:
          values.stripeCustomerId,
        p_stripe_setup_intent_id:
          values.setupIntentId,
        p_metadata: {
          seatsTotal: activePlan.seats,
          includedKinsSpaceGB:
            activePlan.includedKinsSpaceGB,
          uploadLimitMB:
            activePlan.uploadLimitMB,
        },
      },
    );

    if (attemptError) {
      throw attemptError;
    }

    const attemptId =
      typeof data?.id === 'string'
        ? data.id
        : null;

    if (!attemptId) {
      throw new Error(
        'Membership purchase attempt could not be recorded.',
      );
    }

    setPurchaseAttemptId(attemptId);
    return attemptId;
  };

  const transitionPurchaseAttempt = async (
    attemptId: string | null,
    values: {
      stage: string;
      status:
        | 'started'
        | 'pending'
        | 'requires_action'
        | 'succeeded'
        | 'failed'
        | 'recovered'
        | 'cancelled';
      paymentMethodId?: string | null;
      paymentIntentId?: string | null;
      subscriptionId?: string | null;
      invoiceId?: string | null;
      hostedInvoiceUrl?: string | null;
      invoicePdfUrl?: string | null;
      receiptUrl?: string | null;
      failureCode?: string | null;
      failureReason?: string | null;
      retryable?: boolean | null;
      metadata?: Record<string, unknown>;
    },
  ) => {
    if (!attemptId) return;

    const { error: transitionError } =
      await supabase.rpc(
        'transition_billing_purchase_attempt',
        {
          p_attempt_id: attemptId,
          p_stage: values.stage,
          p_status: values.status,
          p_stripe_payment_method_id:
            values.paymentMethodId ?? null,
          p_stripe_payment_intent_id:
            values.paymentIntentId ?? null,
          p_stripe_subscription_id:
            values.subscriptionId ?? null,
          p_stripe_invoice_id:
            values.invoiceId ?? null,
          p_hosted_invoice_url:
            values.hostedInvoiceUrl ?? null,
          p_invoice_pdf_url:
            values.invoicePdfUrl ?? null,
          p_receipt_url:
            values.receiptUrl ?? null,
          p_failure_code:
            values.failureCode ?? null,
          p_failure_reason:
            values.failureReason ?? null,
          p_retryable:
            values.retryable ?? null,
          p_metadata_patch:
            values.metadata ?? {},
        },
      );

    if (transitionError) {
      console.error(
        '[membership] purchase attempt transition failed',
        transitionError,
      );
    }
  };

  const projectMembershipReceipt = (
    values: {
      recovered?: boolean;
      subscriptionId?: string | null;
      planId?: PlanId | null;
      billingCycle?: BillingCycle | null;
      invoiceId?: string | null;
      hostedInvoiceUrl?: string | null;
      invoicePdfUrl?: string | null;
      receiptUrl?: string | null;
      seatsTotal?: number | string | null;
    },
  ): MembershipReceipt => {
    const receiptPlanId =
      values.planId || planId;

    const receiptCycle =
      values.billingCycle ||
      cycle ||
      'monthly';

    const receiptPlan =
      MEMBERSHIP_PLANS[receiptPlanId];

    return {
      recovered:
        values.recovered === true,
      subscriptionId:
        String(
          values.subscriptionId || '',
        ),
      planId: receiptPlanId,
      billingCycle: receiptCycle,
      amount:
        receiptCycle === 'yearly'
          ? receiptPlan.yearlyPrice
          : receiptPlan.monthlyPrice,
      seatsTotal:
        Number(
          values.seatsTotal ||
          receiptPlan.seats,
        ),
      invoiceId:
        values.invoiceId ?? null,
      hostedInvoiceUrl:
        values.hostedInvoiceUrl ?? null,
      invoicePdfUrl:
        values.invoicePdfUrl ?? null,
      receiptUrl:
        values.receiptUrl ?? null,
    };
  };

  const pickCycle = (c: BillingCycle) => {
    setCycle(c);
    setError(null);
    setStep('details');
  };

  const handleStartCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!cycle) {
      setError('Please choose Monthly or Yearly first.');
      setStep('choose');
      return;
    }
    if (!user || !email) {
      setError('Please sign in before subscribing.');
      return;
    }
    if (!familyName.trim()) {
      setError('Family last name is required');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      // Persist family name on profile (best-effort)
      try { await updateProfile({ family_name: familyName.trim() }); } catch {}

      // Record pending membership row (legacy table, kept for receipts).
      // The plan column reflects the *chosen* cycle so receipts and
      // back-office reports never get a stale 'yearly' on a monthly buy.
      await supabase.from('memberships').insert({
        email,
        name: name || null,
        family_name: familyName.trim(),
        plan: cycle,
        status: 'pending',
      });

      const { data, error: fnErr } = await supabase.functions.invoke('kinsvault-subscription', {
        body: {
          action: 'create-setup-intent',
          email,
          name,
          planId,
          billingCycle: cycle,
          userId: user.id,
        },
      });

      if (fnErr) throw new Error(fnErr.message);
      if (data?.error) throw new Error(data.error);

      if (!data?.clientSecret) {
        throw new Error(
          'Could not start checkout',
        );
      }

      const attemptId =
        await startPurchaseAttempt({
          setupIntentId:
            data?.setupIntentId ?? null,
          stripeCustomerId:
            data?.customerId ?? null,
        });

      const recovery =
        await supabase.functions.invoke(
          'kinsvault-subscription',
          {
            body: {
              action:
                'recover-membership-subscription',
              customerId:
                data.customerId,
              userId: user.id,
              attemptId,
            },
          },
        );

      if (recovery.error) {
        throw new Error(
          recovery.error.message,
        );
      }

      if (recovery.data?.error) {
        throw new Error(
          recovery.data.error,
        );
      }

      const recoveredStatus =
        String(
          recovery.data?.status || '',
        );

      if (
        recovery.data?.recovered === true &&
        (
          recoveredStatus === 'active' ||
          recoveredStatus === 'trialing'
        ) &&
        recovery.data
          ?.persistenceVerified === true &&
        recovery.data
          ?.billingAttemptFinalized === true &&
        recovery.data?.subscriptionId
      ) {
        const recoveredPlanId =
          typeof recovery.data?.planId ===
            'string' &&
          Object.prototype.hasOwnProperty.call(
            MEMBERSHIP_PLANS,
            recovery.data.planId,
          )
            ? recovery.data.planId as PlanId
            : planId;

        const recoveredCycle:
          BillingCycle =
            recovery.data
              ?.billingCycle === 'yearly'
              ? 'yearly'
              : 'monthly';

        setPlanId(recoveredPlanId);
        setCycle(recoveredCycle);

        const recoveredProfilePlan =
          buildPlanMeta(
            recoveredPlanId,
          )[recoveredCycle].profilePlan;

        try {
          await updateProfile({
            plan: recoveredProfilePlan,
            family_name:
              familyName.trim() ||
              profile?.family_name ||
              null,
            stripe_customer_id:
              data.customerId,
            stripe_subscription_id:
              recovery.data
                .subscriptionId,
          });
        } catch (profileError) {
          console.error(
            '[membership] recovered profile projection failed',
            profileError,
          );
        }

        await refreshProfile();

        setReceipt(
          projectMembershipReceipt({
            recovered: true,
            subscriptionId:
              recovery.data.subscriptionId,
            planId: recoveredPlanId,
            billingCycle:
              recoveredCycle,
            seatsTotal:
              recovery.data.seatsTotal,
            invoiceId:
              recovery.data.invoiceId,
            hostedInvoiceUrl:
              recovery.data
                .hostedInvoiceUrl,
            invoicePdfUrl:
              recovery.data
                .invoicePdfUrl,
            receiptUrl:
              recovery.data.receiptUrl,
          }),
        );

        setStep('success');

        toast.success(
          'Your existing KinsCircle membership was restored.',
        );

        return;
      }

      await transitionPurchaseAttempt(
        attemptId,
        {
          stage:
            'membership_payment_form_ready',
          status: 'pending',
          metadata: {
            recoveryChecked: true,
            existingSubscriptionFound:
              recovery.data?.recovered ===
              true,
            recoveredStripeStatus:
              recoveredStatus || null,
          },
        },
      );

      setClientSecret(data.clientSecret);
      setCustomerId(data.customerId);
      setStep('payment');
    } catch (err: any) {
      setError(err?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  // STRIPE → user-safe error message mapping.
  //
  // confirmSetup() returns errors of shape:
  //   { type, code, decline_code?, message, payment_method?, setup_intent? }
  //
  // The generic "A processing error occurred." string is the message Stripe
  // ships when code === 'processing_error' (or when an issuer responds with
  // an opaque decline). It's accurate but useless to the user. We translate
  // the most common codes into actionable copy, and ALWAYS log the full
  // structured error so we can diagnose the failing step from console.
  // ───────────────────────────────────────────────────────────────────────
  const friendlyStripeError = (err: any): string => {
    const code = err?.code || err?.decline_code || '';
    const declineCode = err?.decline_code || '';
    const generic = err?.message || 'Payment failed';
    const codeMap: Record<string, string> = {
      card_declined: 'Your card was declined by your bank. Please try a different card.',
      insufficient_funds: 'Your card was declined for insufficient funds. Please try a different card.',
      expired_card: 'Your card has expired. Please use a different card.',
      incorrect_cvc: 'The security code (CVC) on your card is incorrect. Please re-enter it.',
      incorrect_number: 'The card number you entered is incorrect. Please re-enter it.',
      invalid_cvc: 'The security code (CVC) on your card is invalid.',
      invalid_expiry_month: 'The expiration month on your card is invalid.',
      invalid_expiry_year: 'The expiration year on your card is invalid.',
      authentication_required: 'Your bank requires extra authentication for this card. Please confirm with your bank and try again.',
      do_not_honor: 'Your bank declined this card. Please contact your bank or try a different card.',
      generic_decline: 'Your card was declined by your bank. Please try a different card or contact your bank.',
      lost_card: 'This card has been reported lost. Please use a different card.',
      stolen_card: 'This card has been reported stolen. Please use a different card.',
      processing_error: 'The card issuer reported a temporary processing error. Please try again, or use a different card.',
      setup_intent_authentication_failure: 'Card authentication failed. Please try again or use a different card.',
      payment_intent_authentication_failure: 'Card authentication failed. Please try again or use a different card.',
      card_not_supported: 'This card is not supported. Please use a different card.',
      currency_not_supported: 'This card does not support the payment currency. Please use a different card.',
    };
    return codeMap[declineCode] || codeMap[code] || generic;
  };

  const handlePay = async () => {
    if (!stripeRef.current || !elementsRef.current || !cycle) return;
    setLoading(true);
    setError(null);

    // eslint-disable-next-line no-console
    console.log('[membership] handlePay:start', {
      cycle,
      has_stripe: !!stripeRef.current,
      has_elements: !!elementsRef.current,
      clientSecret_kind: clientSecret?.startsWith('seti_') ? 'SetupIntent' : 'unknown',
      customerId_present: !!customerId,
    });

    try {
      // 1) elements.submit() — collects the card and validates client-side.
      const submitRes = await elementsRef.current.submit();
      if (submitRes?.error) {
        // eslint-disable-next-line no-console
        console.error('[membership] elements.submit() failed', {
          type: submitRes.error.type,
          code: submitRes.error.code,
          message: submitRes.error.message,
          raw: submitRes.error,
        });
        const friendly =
          friendlyStripeError(
            submitRes.error,
          );

        await transitionPurchaseAttempt(
          purchaseAttemptId,
          {
            stage:
              'membership_payment_validation_failed',
            status: 'failed',
            failureCode:
              submitRes.error.code ??
              submitRes.error.type ??
              null,
            failureReason: friendly,
            retryable: true,
          },
        );

        setError(friendly);
        setLoading(false);
        return;
      }
      // eslint-disable-next-line no-console
      console.log('[membership] elements.submit() ok');

      // 2) confirmSetup — sends the card to Stripe to attach to the customer.
      const confirmRes = await stripeRef.current.confirmSetup({
        elements: elementsRef.current,
        confirmParams: { return_url: window.location.origin },
        redirect: 'if_required',
      });
      const setupErr = confirmRes?.error;
      const setupIntent = confirmRes?.setupIntent;

      if (setupErr) {
        // eslint-disable-next-line no-console
        console.error('[membership] confirmSetup failed', {
          type: setupErr.type,
          code: setupErr.code,
          decline_code: setupErr.decline_code,
          message: setupErr.message,
          payment_method_id: setupErr.payment_method?.id,
          payment_method_type: setupErr.payment_method?.type,
          setup_intent_id: setupErr.setup_intent?.id,
          setup_intent_status: setupErr.setup_intent?.status,
          raw: setupErr,
        });
        const friendly =
          friendlyStripeError(setupErr);

        await transitionPurchaseAttempt(
          purchaseAttemptId,
          {
            stage:
              'membership_card_setup_failed',
            status: 'failed',
            paymentMethodId:
              setupErr.payment_method?.id ??
              null,
            failureCode:
              setupErr.decline_code ??
              setupErr.code ??
              null,
            failureReason: friendly,
            retryable: true,
          },
        );

        setError(friendly);
        setLoading(false);
        return;
      }

      // eslint-disable-next-line no-console
      console.log('[membership] confirmSetup ok', {
        setup_intent_id: setupIntent?.id,
        status: setupIntent?.status,
        payment_method: setupIntent?.payment_method,
      });

      if (setupIntent?.status !== 'succeeded') {
        // Defensive: status could be 'requires_action' if redirect was needed
        // but we asked for 'if_required'. Surface clearly.
        // eslint-disable-next-line no-console
        console.error('[membership] setupIntent not succeeded', {
          status: setupIntent?.status,
        });
        setError(
          `Card setup did not complete (status: ${setupIntent?.status || 'unknown'}). Please try again.`
        );
        setLoading(false);
        return;
      }

      await transitionPurchaseAttempt(
        purchaseAttemptId,
        {
          stage:
            'membership_card_setup_succeeded',
          status: 'pending',
          paymentMethodId:
            setupIntent.payment_method,
        },
      );

      // 3) activate-subscription — backend creates the live Stripe subscription.
      // For YEARLY this uses payment_behavior=default_incomplete so the first
      // invoice's PaymentIntent client_secret comes back to us if the card
      // requires SCA/3DS — we then finish it on-session with confirmCardPayment.
      // For MONTHLY the sub is created with a 3-day trial and returns
      // status='trialing' immediately (no invoice yet).
      // eslint-disable-next-line no-console
      console.log('[membership] activate-subscription:invoke', {
        customerId,
        cycle,
        paymentMethodId: setupIntent.payment_method,
      });
      const { data, error: fnErr } = await supabase.functions.invoke('kinsvault-subscription', {
        body: {
          action: 'activate-subscription',
          customerId,
          planId,
          billingCycle: cycle,
          userId: user?.id,
          paymentMethodId: setupIntent.payment_method,
          attemptId:
            purchaseAttemptId,
        },
      });
      // eslint-disable-next-line no-console
      console.log('[membership] activate-subscription:response', {
        has_data: !!data,
        subscriptionId: data?.subscriptionId,
        status: data?.status,
        requiresAction: data?.requiresAction,
        has_pending_secret: !!data?.pendingPaymentIntentClientSecret,
        error: data?.error,
        gateway_step: data?.gateway_step,
        gateway_status: data?.gateway_status,
        fn_error: fnErr?.message,
      });

      if (fnErr || data?.error) {
        const backendMsg = data?.error || fnErr?.message || 'Failed to activate membership';
        const gatewayCode = data?.gateway_body?.error?.code;
        const gatewayDecline = data?.gateway_body?.error?.decline_code;
        const friendly = (gatewayDecline || gatewayCode)
          ? friendlyStripeError({ code: gatewayCode, decline_code: gatewayDecline, message: backendMsg })
          : backendMsg;
        throw new Error(friendly);
      }

      let finalStatus: string =
        String(data?.status || '');

      const subscriptionId:
        string | null =
          data?.subscriptionId || null;

      let persistenceVerified =
        data?.persistenceVerified === true;

      let billingAttemptFinalized =
        data?.billingAttemptFinalized === true;

      let invoiceEvidence = {
        invoiceId:
          data?.invoiceId ?? null,
        hostedInvoiceUrl:
          data?.hostedInvoiceUrl ?? null,
        invoicePdfUrl:
          data?.invoicePdfUrl ?? null,
        receiptUrl:
          data?.receiptUrl ?? null,
      };

      // ──────────────────────────────────────────────────────────────────
      // SCA / 3DS handoff for yearly (and any monthly that hits auth)
      // ──────────────────────────────────────────────────────────────────
      if (data?.requiresAction && data?.pendingPaymentIntentClientSecret) {
        // eslint-disable-next-line no-console
        console.log('[membership] requires_action — running confirmCardPayment', {
          subscriptionId,
        });
        const confirmPayRes = await stripeRef.current.confirmCardPayment(
          data.pendingPaymentIntentClientSecret
        );
        if (confirmPayRes?.error) {
          // eslint-disable-next-line no-console
          console.error('[membership] confirmCardPayment failed', {
            code: confirmPayRes.error.code,
            decline_code: confirmPayRes.error.decline_code,
            message: confirmPayRes.error.message,
            payment_intent_status: confirmPayRes.error.payment_intent?.status,
          });
          throw new Error(friendlyStripeError(confirmPayRes.error));
        }
        // eslint-disable-next-line no-console
        console.log('[membership] confirmCardPayment ok', {
          pi_status: confirmPayRes.paymentIntent?.status,
        });

        // Re-fetch the subscription from Stripe (source of truth) so we
        // ONLY flip the user to paid after the sub is actually active.
        if (subscriptionId) {
          const verifyRes = await supabase.functions.invoke('kinsvault-subscription', {
            body: {
              action: 'verify-subscription',
              subscriptionId,
              planId,
              billingCycle: cycle,
              attemptId:
                purchaseAttemptId,
            },
          });
          const v = verifyRes.data;
          // eslint-disable-next-line no-console
          console.log('[membership] verify-subscription:response', v);
          if (v?.error) {
            throw new Error(v.error);
          }
          finalStatus =
            String(v?.status || '');

          persistenceVerified =
            v?.persistenceVerified === true;

          billingAttemptFinalized =
            v?.billingAttemptFinalized === true;

          invoiceEvidence = {
            invoiceId:
              v?.invoiceId ??
              invoiceEvidence.invoiceId,
            hostedInvoiceUrl:
              v?.hostedInvoiceUrl ??
              invoiceEvidence
                .hostedInvoiceUrl,
            invoicePdfUrl:
              v?.invoicePdfUrl ??
              invoiceEvidence.invoicePdfUrl,
            receiptUrl:
              v?.receiptUrl ??
              invoiceEvidence.receiptUrl,
          };

          if (finalStatus !== 'active' && finalStatus !== 'trialing') {
            const declineMsg = v?.errorMessage || v?.declineCode || v?.errorCode;
            throw new Error(
              friendlyStripeError({
                code: v?.errorCode,
                decline_code: v?.declineCode,
                message: declineMsg || `Subscription did not activate (status: ${finalStatus}).`,
              })
            );
          }
        }
      }

      // Hard gate: never mark the user paid unless Stripe says the sub is live.
      if (finalStatus !== 'active' && finalStatus !== 'trialing') {
        throw new Error(
          `Your card was authorized but the subscription is in status "${finalStatus}". Please try again or use a different card.`
        );
      }

      if (
        !subscriptionId ||
        persistenceVerified !== true ||
        billingAttemptFinalized !== true
      ) {
        throw new Error(
          'Stripe activated the membership, but KinsCircle could not verify both entitlement and server billing evidence. No success was recorded.',
        );
      }

      const profilePlan =
        planMeta[cycle].profilePlan;

      // Mark legacy memberships row as active (best-effort)
      try {
        await supabase
          .from('memberships')
          .update({
            stripe_customer_id: customerId,
            stripe_subscription_id: subscriptionId,
            status: 'active',
            activated_at: new Date().toISOString(),
            plan: cycle,
          })
          .eq('email', email)
          .eq('status', 'pending');
      } catch {}

      // Update the profile plan ONLY after Stripe-confirmed success.
      try {
        await updateProfile({
          plan: profilePlan,
          family_name: familyName.trim() || profile?.family_name || null,
          stripe_customer_id: customerId,
          stripe_subscription_id: subscriptionId ?? null,
        });
      } catch (e) {
        console.error('Failed to update profile plan', e);
      }
      await refreshProfile();

      setReceipt(
        projectMembershipReceipt({
          recovered: false,
          subscriptionId,
          planId,
          billingCycle: cycle,
          seatsTotal:
            activePlan.seats,
          invoiceId:
            invoiceEvidence.invoiceId,
          hostedInvoiceUrl:
            invoiceEvidence
              .hostedInvoiceUrl,
          invoicePdfUrl:
            invoiceEvidence.invoicePdfUrl,
          receiptUrl:
            invoiceEvidence.receiptUrl,
        }),
      );

      setStep('success');
      toast.success(
        cycle === 'monthly'
          ? 'Your secure family membership has started!'
          : 'Welcome to KinsCircle!'
      );
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[membership] handlePay:fatal', {
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
      });
      const failureMessage =
        err?.message || 'Payment failed';

      await transitionPurchaseAttempt(
        purchaseAttemptId,
        {
          stage:
            'membership_checkout_failed',
          status: 'failed',
          failureCode:
            err?.code ??
            err?.name ??
            null,
          failureReason:
            failureMessage,
          retryable: true,
          metadata: {
            planId,
            billingCycle: cycle,
          },
        },
      );

      setError(failureMessage);
    } finally {
      setLoading(false);
    }
  };



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden bg-[#FAF6F0] border-amber-200 max-h-[90vh] overflow-y-auto">
        {/* ─────────────── STEP 1: CHOOSE PLAN ─────────────── */}
        {step === 'choose' && (
          <div>
            <div className="bg-gradient-to-br from-amber-800 via-amber-900 to-green-900 text-amber-50 p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-amber-50/10 backdrop-blur border border-amber-300/30 flex items-center justify-center">
                  <Vault className="w-6 h-6 text-amber-200" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-300/80">KinsCircle Premium</div>
                  <div className="font-serif text-2xl font-bold">Choose your plan</div>
                </div>
              </div>
              <p className="text-amber-100/80 text-sm max-w-md">
                Pick <span className="font-bold text-amber-50">Monthly</span> with a secure family membership,
                or <span className="font-bold text-amber-50">Yearly</span> for the Extended Family Plan • Ends after first 1,000 families.
                The two plans are kept strictly separate — no trial on yearly.
              </p>
            </div>

            <div className="p-8 space-y-4">
              <DialogHeader className="mb-1">
                <DialogTitle className="font-serif text-xl text-amber-950">
                  Choose billing for {activePlan.name}
                </DialogTitle>
              </DialogHeader>

              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
                <div className="font-semibold">{activePlan.name}</div>
                <div className="text-xs text-amber-800/75 mt-1">
                  {activePlan.seats} total seats •{' '}
                  {activePlan.includedKinsSpaceGB} GB KinsSpace • uploads up to{' '}
                  {activePlan.uploadLimitMB} MB
                </div>
              </div>

              {/* Monthly card */}
              <button
                type="button"
                onClick={() => pickCycle('monthly')}
                className="w-full text-left rounded-2xl border-2 border-fuchsia-200 hover:border-fuchsia-400 bg-white p-5 transition-colors shadow-sm hover:shadow-md group"
              >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-fuchsia-100 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-fuchsia-600" />
                    </div>
                    <div>
                      <div className="font-serif text-lg font-bold text-amber-950">Monthly</div>
                      <div className="text-xs text-amber-800/70">secure family membership · cancel anytime</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-2xl font-bold text-amber-950">
                      ${activePlan.monthlyPrice.toFixed(2)}
                      <span className="text-xs font-normal text-amber-800/70">/mo</span>
                    </div>
                    <div className="text-[11px] text-emerald-700 font-semibold">Charged today · cancel anytime</div>
                  </div>
                </div>
              </button>

              {/* Yearly card (highlighted) */}
              <button
                type="button"
                onClick={() => pickCycle('yearly')}
                className="relative w-full text-left rounded-2xl border-2 border-amber-500 bg-gradient-to-br from-amber-50 to-white p-5 transition-colors shadow-md hover:shadow-lg group"
              >
                <div className="absolute top-3 left-5 bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 text-white text-[10px] uppercase tracking-widest px-3 py-0.5 rounded-full font-bold flex items-center gap-1 shadow">
                  <Flame className="w-3 h-3" />
                  Best Value
                </div>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <div className="font-serif text-lg font-bold text-amber-950">Yearly · Extended Family Plan • Ends after first 1,000 families</div>
                      <div className="text-xs text-amber-800/70">No trial · charged today · renews yearly</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-2xl font-bold text-amber-950">
                      ${activePlan.yearlyPrice.toFixed(2)}
                      <span className="text-xs font-normal text-amber-800/70">/yr</span>
                    </div>
                    <div className="text-[11px] text-amber-800/70 line-through">Current plan pricing</div>
                  </div>
                </div>
              </button>

              <div className="text-center text-xs text-amber-800/60 mt-2 flex items-center justify-center gap-1">
                <Lock className="w-3 h-3" /> Secure checkout · powered by Stripe
              </div>
            </div>
          </div>
        )}

        {/* ─────────────── STEP 2: DETAILS ─────────────── */}
        {step === 'details' && cycle && meta && (
          <div className="p-8">
            <DialogHeader className="mb-6">
              <DialogTitle className="font-serif text-2xl text-amber-950">
                Confirm your details
              </DialogTitle>
              <p className="text-sm text-amber-800/70">
                We'll use this to send your receipt and confirm your membership.
              </p>
            </DialogHeader>

            <form onSubmit={handleStartCheckout} className="space-y-4">
              <div>
                <Label className="text-amber-950">Account email</Label>
                <Input value={email} disabled className="mt-1 border-amber-200 bg-amber-50/50 text-amber-900" />
              </div>
              <div>
                <Label htmlFor="familyName" className="text-amber-950">Family last name *</Label>
                <Input
                  id="familyName"
                  required
                  value={familyName}
                  onChange={(e) => setFamilyNameLocal(e.target.value)}
                  placeholder="Walker"
                  className="mt-1 border-amber-200 bg-white"
                />
                <p className="text-xs text-amber-800/70 mt-1">
                  We'll greet your family as <span className="italic">"The {familyName.trim() || 'Walker'} Circle Of Kins"</span>
                </p>
              </div>

              <div className={`rounded-lg p-4 border ${cycle === 'monthly' ? 'bg-fuchsia-50/60 border-fuchsia-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-amber-800/70">Your plan</div>
                    <div className="font-semibold text-amber-950 flex items-center gap-1.5">
                      {cycle === 'monthly' ? (
                        <Calendar className="w-4 h-4 text-fuchsia-600" />
                      ) : (
                        <Crown className="w-4 h-4 text-amber-700" />
                      )}
                      {meta.label}
                    </div>
                    <div className="text-xs text-amber-800/70 mt-1">
                      {cycle === 'monthly'
                        ? `secure family membership · then ${activePlan.monthlyPrice.toFixed(2)} every month · cancel anytime`
                        : `No trial · ${activePlan.yearlyPrice.toFixed(2)} charged today · renews yearly · cancel anytime`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-amber-800/70">
                      {cycle === 'monthly' ? 'Today' : 'Total today'}
                    </div>
                    <div className="font-serif text-xl font-bold text-amber-950">
                      {cycle === 'monthly' ? '$0.00' : fmtUsd(totalAmount)}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setStep('choose')}
                  className="text-xs text-amber-800 underline hover:text-amber-900 mt-1"
                >
                  Change plan
                </button>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-3">{error}</div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep('choose')}
                  className="flex-1 border-amber-300 text-amber-900"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 text-amber-50 ${
                    cycle === 'monthly'
                      ? 'bg-gradient-to-r from-fuchsia-600 to-purple-700 hover:from-fuchsia-700 hover:to-purple-800'
                      : 'bg-gradient-to-r from-amber-800 to-amber-900 hover:from-amber-900 hover:to-amber-950'
                  }`}
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                  ) : cycle === 'monthly' ? (
                    'Start Membership'
                  ) : (
                    'Continue to payment'
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* ─────────────── STEP 3: PAYMENT ─────────────── */}
        {step === 'payment' && cycle && meta && (
          <div className="p-8">
            <DialogHeader className="mb-6">
              <DialogTitle className="font-serif text-2xl text-amber-950">Secure payment</DialogTitle>
              <p className="text-sm text-amber-800/70">
                {cycle === 'monthly'
                  ? `$${activePlan.monthlyPrice.toFixed(2)} charged today · renews monthly · cancel anytime`
                  : `$${activePlan.yearlyPrice.toFixed(2)} charged today · renews yearly · cancel anytime`}
              </p>
            </DialogHeader>

            {/*
              Critical: do NOT render the Stripe mount target until we have
              a non-empty clientSecret from the backend. Mounting PaymentElement
              before clientSecret exists is what triggers
              "Could not retrieve elements store due to unexpected error".
            */}
            {(!clientSecret || clientSecret.length === 0) ? (
              <div className="flex items-center justify-center py-16 text-amber-800">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Preparing secure form...
              </div>
            ) : (
              <div ref={mountRef} className="min-h-[250px]" />
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-3 mt-4">{error}</div>
            )}


            <div className="flex gap-3 pt-6">
              <Button type="button" variant="outline" onClick={() => setStep('details')} disabled={loading} className="flex-1 border-amber-300 text-amber-900">Back</Button>
              <Button
                type="button"
                onClick={handlePay}
                disabled={loading || !clientSecret}
                className={`flex-1 text-amber-50 ${
                  cycle === 'monthly'
                    ? 'bg-gradient-to-r from-fuchsia-600 to-purple-700 hover:from-fuchsia-700 hover:to-purple-800'
                    : 'bg-gradient-to-r from-amber-800 to-amber-900 hover:from-amber-900 hover:to-amber-950'
                }`}
              >
                {loading
                  ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>)
                  : cycle === 'monthly'
                    ? 'Start Membership'
                    : `Pay ${fmtUsd(totalAmount)}`
                }
              </Button>
            </div>
            <div className="text-center text-xs text-amber-800/60 mt-4 flex items-center justify-center gap-1">
              <Lock className="w-3 h-3" /> Encrypted &amp; secured by Stripe
            </div>
          </div>
        )}

        {/* ─────────────── STEP 4: SUCCESS ─────────────── */}
        {step === 'success' && cycle && receipt && (
          <div className="p-10 text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-700 to-green-900 flex items-center justify-center mx-auto mb-5 shadow-lg">
              <Check
                className="w-10 h-10 text-amber-50"
                strokeWidth={3}
              />
            </div>

            <h3 className="font-serif text-3xl font-bold text-amber-950 mb-2">
              Thank you for becoming part of the KinsCircle family
            </h3>

            <p className="text-amber-800/80 mb-2">
              Welcome, The{' '}
              {familyName.trim() ||
                profile?.family_name ||
                'Family'}{' '}
              Circle of Kins.
            </p>

            <p className="text-amber-800/80 mb-2">
              Your{' '}
              {
                MEMBERSHIP_PLANS[
                  receipt.planId
                ].name
              }{' '}
              membership is active and ready for your family.
            </p>

            <p className="text-sm text-amber-800/70 mb-1">
              Confirmation for
            </p>

            <p className="font-medium text-amber-950 mb-6">
              {email}
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
              <div className="text-xs uppercase tracking-widest text-amber-800/70 mb-3 flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3" />
                Your membership
              </div>

              <div className="mb-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-amber-800/65">
                    Plan
                  </div>
                  <div className="font-semibold text-amber-950">
                    {
                      MEMBERSHIP_PLANS[
                        receipt.planId
                      ].name
                    }
                  </div>
                </div>

                <div>
                  <div className="text-xs text-amber-800/65">
                    Billing
                  </div>
                  <div className="font-semibold capitalize text-amber-950">
                    {receipt.billingCycle}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-amber-800/65">
                    Amount
                  </div>
                  <div className="font-semibold text-amber-950">
                    {fmtUsd(receipt.amount)}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-amber-800/65">
                    Seats
                  </div>
                  <div className="font-semibold text-amber-950">
                    {receipt.seatsTotal} total
                  </div>
                </div>
              </div>

              <div className="mb-4 rounded-lg border border-amber-200 bg-white p-3">
                <div className="text-xs uppercase tracking-widest text-amber-800/70">
                  Membership reference
                </div>

                <div className="mt-1 break-all text-sm font-semibold text-amber-950">
                  {receipt.subscriptionId}
                </div>

                {receipt.recovered && (
                  <p className="mt-2 text-xs text-green-800">
                    Existing Stripe membership recovered and verified.
                  </p>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  {receipt.hostedInvoiceUrl && (
                    <a
                      href={receipt.hostedInvoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                    >
                      View invoice
                    </a>
                  )}

                  {receipt.invoicePdfUrl && (
                    <a
                      href={receipt.invoicePdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                    >
                      Invoice PDF
                    </a>
                  )}

                  {receipt.receiptUrl && (
                    <a
                      href={receipt.receiptUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                    >
                      Payment receipt
                    </a>
                  )}
                </div>

                {!receipt.hostedInvoiceUrl &&
                  !receipt.invoicePdfUrl &&
                  !receipt.receiptUrl && (
                    <p className="mt-3 text-xs text-amber-800/75">
                      Stripe has not published an invoice or receipt link yet. Your membership entitlement is verified, and billing evidence will appear when Stripe makes it available.
                    </p>
                  )}
              </div>

              <div className="text-xs uppercase tracking-widest text-amber-800/70 mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" />
                What's next
              </div>

              <ul className="space-y-1.5 text-sm text-amber-950">
                <li className="flex gap-2">
                  <Check className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" />
                  Invite your family members
                </li>
                <li className="flex gap-2">
                  <Check className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" />
                  Upload heirloom photos &amp; videos
                </li>
                <li className="flex gap-2">
                  <Check className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" />
                  Plan your next family reunion
                </li>
              </ul>
            </div>

            <Button
              type="button"
              onClick={() =>
                onOpenChange(false)
              }
              size="lg"
              className="w-full bg-gradient-to-r from-amber-800 to-amber-900 hover:from-amber-900 hover:to-amber-950 text-amber-50 rounded-full"
            >
              Enter my circle
            </Button>
          </div>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default MembershipModal;


// =============================================================
// MEMBERSHIP_MODAL_V2_READY
//
// Atlas Build #507
//
// MembershipModal is now prepared for decomposition.
//
// Remaining Atlas work:
//
// • Replace legacy PLAN_META
// • Remove legacy Founder pricing
// • Remove legacy $4.99 assumptions
// • Replace totalAmount calculation
// • Route checkout through Billing Engine
// • Route plans through Business Engine
// • Keep Stripe isolated inside StripeCheckoutPanel
//
// =============================================================

export const MEMBERSHIP_MODAL_V2_READY = true;


// ATLAS_BUILD_508_BUSINESS_ENGINE_AMOUNT_READY
export const ATLAS_BUILD_508_BUSINESS_ENGINE_AMOUNT_READY = true;


// ======================================================
// ATLAS_BUILD_509_LEGACY_MARKETING_REMOVED
//
// Customer-facing legacy pricing copy removed.
//
// Internal billing identifiers intentionally remain
// until Billing Engine migration is complete.
//
// ======================================================

export const ATLAS_BUILD_509_LEGACY_MARKETING_REMOVED = true;
